/**
 * CRM Search
 *
 * Deterministic matching helpers for CRM people and business search.
 *
 * Primary responsibilities:
 * - Normalize user queries and CRM field values
 * - Rank split-name and accent-insensitive CRM candidates
 *
 * Dependencies:
 * - PostgREST filter helpers
 */

import { buildPostgrestOrFilter } from '@/lib/supabase/postgrest-filters'

export type CrmSearchValue = string | number | null | undefined

type RankedRecord<T> = {
  record: T
  score: number
}

// --------------

export function normalizeCrmSearchText(value: CrmSearchValue) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}@._+-]+/gu, ' ')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

// --------------

export function getCrmSearchTokens(query: string) {
  const normalized = normalizeCrmSearchText(query)
  if (!normalized) return []

  return Array.from(
    new Set(
      normalized
        .split(' ')
        .map((token) => token.trim())
        .filter((token) => token.length > 1)
    )
  )
}

// --------------

export function buildCrmSearchPostgrestOrFilter(columns: string[], query: string) {
  const values = getCrmPostgrestSearchValues(query)

  return buildPostgrestOrFilter(
    values.flatMap((value) =>
      columns.map((column) => ({
        column,
        operator: 'ilike' as const,
        value: `%${value}%`
      }))
    )
  )
}

// --------------

export function rankCrmSearchRecords<T>(
  records: T[],
  query: string,
  getValues: (record: T) => CrmSearchValue[]
) {
  const normalizedQuery = normalizeCrmSearchText(query)
  if (!normalizedQuery) return records

  const tokens = getCrmSearchTokens(query)

  return records
    .map((record): RankedRecord<T> => ({
      record,
      score: scoreCrmSearchRecord({
        normalizedQuery,
        tokens,
        values: getValues(record)
      })
    }))
    .filter((item) => item.score > 0)
    .sort((first, second) => second.score - first.score)
    .map((item) => item.record)
}

// --------------

function getCrmPostgrestSearchValues(query: string) {
  return Array.from(
    new Set(
      [query.trim(), normalizeCrmSearchText(query), ...getCrmSearchTokens(query)]
        .map((value) => value.trim())
        .filter(Boolean)
    )
  )
}

// --------------

function scoreCrmSearchRecord(params: {
  normalizedQuery: string
  tokens: string[]
  values: CrmSearchValue[]
}) {
  const normalizedValues = params.values
    .map(normalizeCrmSearchText)
    .filter(Boolean)
  const haystack = normalizeCrmSearchText(normalizedValues.join(' '))

  if (!haystack) return 0

  let score = 0

  if (normalizedValues.some((value) => value === params.normalizedQuery)) {
    score += 120
  }

  if (normalizedValues.some((value) => value.startsWith(params.normalizedQuery))) {
    score += 90
  }

  if (haystack.includes(params.normalizedQuery)) {
    score += 70
  }

  if (
    params.tokens.length > 0 &&
    params.tokens.every((token) => haystack.includes(token))
  ) {
    score += 45
  }

  score += params.tokens.filter((token) => haystack.includes(token)).length * 6

  return score
}
